import { SubmitHandler, useForm } from 'react-hook-form';
import { User } from '../../../../../types';
import {
  UserKeys,
  updateUserProfileInfo,
  updateUserProfileInfoUserPortal,
} from '../../../../api/users-api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { handleErrors } from '../../../../utils/errors';

import ChangeLogDialog from '../../../../shared/change-log-dialog';
import structuredClone from '@ungap/structured-clone';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  UserViewRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import AddressAccordion from './address-accordion';
import EmailsAccordion from './emails-accordion';
import GenderAccordion from './gender-accordion';
import IdsAccordion from './ids-accordion';
import ImsAccordion from './ims-accordion';
import NotesAccordion from './notes-accordion';
import PhonesAccordion from './phones-accordion';
import RelationsAccordion from './relations-accordion';
import WebsitesAccordion from './websites-accordion';
import OrganizationsAccordion from './organizations-accordion';
import EmployeeIdTextField from './employee-id-textfield';
import WorkPhoneTextField from './work-phone-textfield';
import ManagerEmailTextField from './manager-email-textfield';
import PrimaryOrganizationSection from './primary-organization-section';
import { useLocation } from 'react-router-dom';
import { userPortalPagesConfig } from '../../../../shared/user-portal-pages-config';
import { UserFormData } from '../../add-user-dialog/user-form-page';
import Box from '@mui/material/Box';
import SharedButton from '../../../../shared/buttons/shared-button';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import SharedTabHeader from '../../../../shared/shared-tab-header';
import SharedIconButton from '../../../../shared/buttons/shared-icon-button';
interface ProfileInformationFormProps {
  user: User;
  directoryRoles: string[];
  userViewRoles?: string[];
  changeLogRoles: string[];
}

export default function ProfileInformationForm({
  user,
  userViewRoles,
  directoryRoles,
  changeLogRoles,
}: ProfileInformationFormProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const location = useLocation();

  const userView = location.pathname.includes(
    userPortalPagesConfig.userPortal.link
  );

  const { mutate, isPending: updatingUser } = useMutation({
    mutationFn: (data: Partial<User>) =>
      updateUserProfileInfo(user.primaryEmail || '', data, changeLogMessage),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'User updated successfully!',
      });
      queryClient.setQueryData([UserKeys.User, user.primaryEmail], data);
      setChangeLogMessage('');

      const clonedData = structuredClone(data);

      reset({
        ...data,
        gender: data.gender || { type: '', addressMeAs: '' },
        addresses: data.addresses || [],
        emails: data.emails?.filter((email) => email.type) || [],
        externalIds: clonedData.externalIds?.filter((id) => id.value) || [],
        ims: clonedData.ims?.filter((im) => im.im) || [],
        organizations:
          clonedData.organizations?.map((org) => {
            if (
              !org.type ||
              org.customType?.toLocaleLowerCase() === 'unknown'
            ) {
              org.type = 'unknown';
            } else if (org.customType !== '') {
              org.type = 'custom';
            }
            return org;
          }) || [],
        phones: clonedData.phones?.filter((phone) => phone.value) || [],
        relations:
          clonedData.relations?.filter((relation) => relation.value) || [],
        websites: clonedData.websites?.filter((website) => website.url) || [],
      });
    },
    onError: (error) => {
      handleErrors({
        error: error,
        snackbarMessage: 'There was an issue updating user. Please try again.',
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.profile_information
          .update_profile_information,
        {
          module: ModuleList.modules.userManagement.name,
          section:
            ModuleList.modules.userManagement.sections.profileInformation,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  const { mutate: userPortalMutate, isPending: updatingUserInUserPortal } =
    useMutation({
      mutationFn: (data: Partial<User>) =>
        updateUserProfileInfoUserPortal(user.primaryEmail || '', data),
      onSuccess: (returnedUser) => {
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'User updated successfully!',
        });
        queryClient.setQueryData(
          [UserKeys.User, user.primaryEmail],
          returnedUser
        );
        const clonedData = structuredClone(returnedUser);

        reset({
          ...returnedUser,
          gender: returnedUser.gender || { type: '', addressMeAs: '' },
          addresses: returnedUser.addresses || [],
          emails: returnedUser.emails?.filter((email) => email.type) || [],
          externalIds: clonedData.externalIds?.filter((id) => id.value) || [],
          ims: clonedData.ims?.filter((im) => im.im) || [],
          organizations:
            clonedData.organizations?.map((org) => {
              if (
                !org.type ||
                org.customType?.toLocaleLowerCase() === 'unknown'
              ) {
                org.type = 'unknown';
              } else if (org.customType !== '') {
                org.type = 'custom';
              }
              return org;
            }) || [],
          phones: clonedData.phones?.filter((phone) => phone.value) || [],
          relations:
            clonedData.relations?.filter((relation) => relation.value) || [],
          websites: clonedData.websites?.filter((website) => website.url) || [],
        });
      },
      onError: (error) => {
        handleErrors({
          error: error,
          snackbarMessage:
            'There was an issue updating user. Please try again.',
        });
      },
      onSettled: () => {
        createEvent(
          analytics.userView.profile_information.update_profile_information,
          {
            module: ModuleList.modules.userView.name,
            section: ModuleList.modules.userView.sections.profileInformation,
          }
        );
      },
    });

  const clonedUser: User = structuredClone(user);

  const {
    control,
    handleSubmit,
    reset,
    formState,
    setValue,
    watch,
    getValues,
  } = useForm<UserFormData>({
    defaultValues: {
      ...user,
      gender: user.gender || { type: '', addressMeAs: '' },
      addresses: user.addresses || [],
      emails: user.emails?.filter((email) => email.type) || [],
      externalIds: clonedUser.externalIds?.filter((id) => id.value) || [],
      ims: clonedUser.ims?.filter((im) => im.im) || [],
      organizations:
        clonedUser.organizations?.map((org) => {
          if (!org.type || org.customType?.toLocaleLowerCase() === 'unknown') {
            org.type = 'unknown';
          } else if (org.customType !== '') {
            org.type = 'custom';
          }
          return org;
        }) || [],
      phones: clonedUser.phones?.filter((phone) => phone.value) || [],
      relations:
        clonedUser.relations?.filter((relation) => relation.value) || [],
      websites: clonedUser.websites?.filter((website) => website.url) || [],
    },
  });

  // scroll to errors in accordion
  useEffect(() => {
    if (!formState.errors) return;
    const firstErrorField = Object.keys(formState.errors)[0];
    if (firstErrorField) {
      const errorElement = document.querySelector(`[name=${firstErrorField}]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [formState]);

  const { isDirty, isLoading, errors } = formState;

  function mutateData() {
    const correctedOrgs = getValues('organizations')?.map((org) => {
      if (org.type === 'unknown') {
        delete org.type;
        org.customType = 'Unknown';
      } else if (org.type === 'custom') {
        org.type = 'unknown';
      } else if (org.type !== 'custom' && org.customType !== '') {
        org.customType = '';
      }
      return org;
    });

    /* 
       This is done too allow if the user used a third party tool that populated any of the values with null values.
       We don't display them and exclude them from the form but then add them back in on submit so they can keep there null values.
    */
    const nullRelations =
      clonedUser.relations?.filter((relation) => relation.value === null) || [];
    const allRelations = [...nullRelations, ...(getValues('relations') || [])];

    const nullPhones =
      clonedUser.phones?.filter((phone) => phone.value === null) || [];
    const allPhones = [...nullPhones, ...(getValues('phones') || [])];

    const nullIms = clonedUser.ims?.filter((im) => im.im === null) || [];
    const allIms = [...nullIms, ...(getValues('ims') || [])];

    const nullWebsites =
      clonedUser.websites?.filter((website) => website.url === null) || [];
    const allWebsites = [...nullWebsites, ...(getValues('websites') || [])];

    const nullExteranlIds =
      clonedUser.externalIds?.filter((id) => id.value === null) || [];
    const allExternalIds = [
      ...nullExteranlIds,
      ...(getValues('externalIds') || []),
    ];

    // Must intialize default to null for back end comparison permissions, DO NOT TOUCH
    const gPanelUser = {
      ...getValues(),
      addresses:
        getValues('addresses')?.length !== 0 ? getValues('addresses') : [],
      organizations: correctedOrgs?.length !== 0 ? correctedOrgs : [],
      externalIds: allExternalIds.length !== 0 ? allExternalIds : [],
      phones: allPhones.length !== 0 ? allPhones : [],
      relations: allRelations.length !== 0 ? allRelations : [],
      ims: allIms.length !== 0 ? allIms : [],
      websites: allWebsites.length !== 0 ? allWebsites : [],
    } as UserFormData;

    if (gPanelUser.gender?.type === '') {
      delete gPanelUser.gender;
    }

    gPanelUser.externalIds?.map((id) => {
      if (id.type !== 'custom' && id.customType !== '') {
        delete id.customType;
      }
    });

    gPanelUser.phones?.map((phone) => {
      if (phone.type !== 'custom' && phone.customType !== '') {
        delete phone.customType;
      }
    });

    gPanelUser.relations?.map((rel) => {
      if (rel.type !== 'custom' && rel.customType !== '') {
        delete rel.customType;
      }
    });

    if (gPanelUser.websites && gPanelUser.websites.length > 0) {
      gPanelUser.websites.map((website) => {
        if (website.type !== 'custom' && website.customType !== '') {
          delete website.customType;
        }
      });
    }

    if (gPanelUser.emails && gPanelUser.emails.length > 0) {
      gPanelUser.emails.map((email) => {
        if (email.type !== 'custom' && email.customType !== '') {
          delete email.customType;
        }
      });
    }

    // need to add back the ones initially filtered out
    const initialEmails = user.emails?.filter((email) => !email.type) || [];
    gPanelUser.emails = [...gPanelUser.emails!, ...initialEmails];

    if (gPanelUser.ims && gPanelUser.ims.length > 0) {
      gPanelUser.ims.map((im) => {
        if (im.type !== 'custom' && im.customType !== '') {
          delete im.customType;
        }
        if (!im.protocol) {
          delete im.protocol;
        }
      });
    }

    if (gPanelUser.addresses && gPanelUser.addresses.length > 0) {
      gPanelUser.addresses.map((address) => {
        if (address.type !== 'custom' && address.customType !== '') {
          delete address.customType;
        }
      });
    }

    return gPanelUser;
  }

  const onSubmit: SubmitHandler<UserFormData> = () => {
    if (!userView) {
      if (
        (formState.dirtyFields.emails &&
          isAbleToSeeUIElement(
            changeLogRoles,
            DirectoryRoles.EditEmailAddress.value
          )) ||
        (formState.dirtyFields.phones &&
          isAbleToSeeUIElement(
            changeLogRoles,
            DirectoryRoles.EditPhoneNumbers.value
          )) ||
        (formState.dirtyFields.organizations &&
          isAbleToSeeUIElement(
            changeLogRoles,
            DirectoryRoles.EditOrganizations.value
          )) ||
        (formState.dirtyFields.addresses &&
          isAbleToSeeUIElement(
            changeLogRoles,
            DirectoryRoles.EditAddresses.value
          )) ||
        (formState.dirtyFields.externalIds &&
          isAbleToSeeUIElement(
            changeLogRoles,
            DirectoryRoles.EditExternalIds.value
          )) ||
        (formState.dirtyFields.ims &&
          isAbleToSeeUIElement(
            changeLogRoles,
            DirectoryRoles.EditInstantMessaging.value
          )) ||
        (formState.dirtyFields.notes &&
          isAbleToSeeUIElement(
            changeLogRoles,
            DirectoryRoles.EditNotes.value
          )) ||
        (formState.dirtyFields.gender &&
          isAbleToSeeUIElement(
            changeLogRoles,
            DirectoryRoles.EditGender.value
          )) ||
        (formState.dirtyFields.relations &&
          isAbleToSeeUIElement(
            changeLogRoles,
            DirectoryRoles.EditRelationships.value
          )) ||
        (formState.dirtyFields.websites &&
          isAbleToSeeUIElement(
            changeLogRoles,
            DirectoryRoles.EditWebsites.value
          )) ||
        (formState.dirtyFields.notes &&
          isAbleToSeeUIElement(changeLogRoles, DirectoryRoles.EditNotes.value))
      ) {
        setOpenChangeLogDialog(true);
      } else {
        const gPanelUser = mutateData();
        mutate(gPanelUser);
      }
    } else {
      const gPanelUser = mutateData();
      userPortalMutate(gPanelUser);
    }
  };

  const disableExternalIds =
    (userView &&
      !isAbleToSeeUIElement(
        userViewRoles!,
        UserViewRoles.EditExternalIds.value
      )) ||
    (!userView &&
      !isAbleToSeeUIElement(
        directoryRoles,
        DirectoryRoles.EditExternalIds.value
      ));

  const disablePhoneNumbers =
    (userView &&
      !isAbleToSeeUIElement(
        userViewRoles!,
        UserViewRoles.EditPhoneNumbers.value
      )) ||
    (!userView &&
      !isAbleToSeeUIElement(
        directoryRoles,
        DirectoryRoles.EditPhoneNumbers.value
      ));

  const disableRelationships =
    (userView &&
      !isAbleToSeeUIElement(
        userViewRoles!,
        UserViewRoles.EditRelationships.value
      )) ||
    (!userView &&
      !isAbleToSeeUIElement(
        directoryRoles,
        DirectoryRoles.EditRelationships.value
      ));

  const disableOrganizations =
    (userView &&
      !isAbleToSeeUIElement(
        userViewRoles!,
        UserViewRoles.EditOrganizations.value
      )) ||
    (!userView &&
      !isAbleToSeeUIElement(
        directoryRoles,
        DirectoryRoles.EditOrganizations.value
      ));

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        sx={{
          position: 'relative',
          height: '100%',
          ...(userView && { p: 2, pt: 1 }),
        }}
      >
        {userView && <SharedTabHeader title="Profile Information" noDivider />}
        <Stack
          flexDirection="row"
          justifyContent="flex-end"
          alignItems="center"
          gap={1}
          sx={
            !userView
              ? { position: 'absolute', top: -60, right: 0 }
              : { position: 'absolute', top: 12, right: 20 }
          }
        >
          <SharedIconButton
            variant="refresh"
            onClick={() => {
              void queryClient.invalidateQueries({ queryKey: ['User'] });
            }}
            tooltipLabel="Refresh"
          />
          <SharedButton
            variant="primary"
            disabled={!isDirty || updatingUser || updatingUserInUserPortal}
            loading={updatingUser || isLoading || updatingUserInUserPortal}
            type="submit"
          >
            Save
          </SharedButton>
        </Stack>

        <Box
          sx={{
            flex: 1,
            height: '100%',
            overflowY: 'auto',
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 'bold', mb: 1, ml: 17 }}>
            Employee Information
          </Typography>
          <Stack
            flexDirection="row"
            flexWrap="wrap"
            gap={4}
            justifyContent="center"
            sx={{ p: 2, width: '100%' }}
          >
            <EmployeeIdTextField
              watch={watch}
              setValue={setValue}
              disabled={disableExternalIds}
            />
            <WorkPhoneTextField
              watch={watch}
              setValue={setValue}
              disabled={disablePhoneNumbers}
            />
            <ManagerEmailTextField
              watch={watch}
              setValue={setValue}
              disabled={disableRelationships}
            />
            <Box sx={{ width: 400 }}></Box>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Typography sx={{ fontSize: 14, fontWeight: 'bold', mb: 1, ml: 17 }}>
            Primary Organization
          </Typography>
          <Stack
            flexDirection="row"
            flexWrap="wrap"
            gap={4}
            justifyContent="center"
            sx={{ p: 2 }}
          >
            <PrimaryOrganizationSection
              watch={watch}
              setValue={setValue}
              disabled={disableOrganizations}
            />

            <Box sx={{ width: 400 }}></Box>
            <Box sx={{ width: 400 }}></Box>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Typography sx={{ fontSize: 14, fontWeight: 'bold', mb: 1, ml: 17 }}>
            Google Attributes
          </Typography>
          <Stack
            sx={{
              p: 2,
              justifyContent: 'center',
              maxWidth: 1000,
              mx: 'auto',
              gap: 2,
            }}
          >
            <AddressAccordion
              control={control}
              errors={errors}
              setValue={setValue}
              getValues={getValues}
              watch={watch}
              disabled={
                userView
                  ? !isAbleToSeeUIElement(
                      userViewRoles!,
                      UserViewRoles.EditAddresses.value
                    )
                  : !isAbleToSeeUIElement(
                      directoryRoles,
                      DirectoryRoles.EditAddresses.value
                    )
              }
            />
            <EmailsAccordion
              control={control}
              errors={errors}
              watch={watch}
              disabled={
                userView
                  ? !isAbleToSeeUIElement(
                      userViewRoles!,
                      UserViewRoles.EditEmailAddress.value
                    )
                  : !isAbleToSeeUIElement(
                      directoryRoles,
                      DirectoryRoles.EditEmailAddress.value
                    )
              }
            />
            <GenderAccordion
              control={control}
              errors={errors}
              watch={watch}
              user={user}
              disabled={
                userView
                  ? !isAbleToSeeUIElement(
                      userViewRoles!,
                      UserViewRoles.EditGender.value
                    )
                  : !isAbleToSeeUIElement(
                      directoryRoles,
                      DirectoryRoles.EditGender.value
                    )
              }
            />
            <IdsAccordion
              control={control}
              watch={watch}
              errors={errors}
              disabled={disableExternalIds}
            />
            <ImsAccordion
              control={control}
              watch={watch}
              errors={errors}
              disabled={
                userView
                  ? !isAbleToSeeUIElement(
                      userViewRoles!,
                      UserViewRoles.EditInstantMessaging.value
                    )
                  : !isAbleToSeeUIElement(
                      directoryRoles,
                      DirectoryRoles.EditInstantMessaging.value
                    )
              }
            />
            <NotesAccordion
              control={control}
              user={user}
              disabled={
                userView
                  ? !isAbleToSeeUIElement(
                      userViewRoles!,
                      UserViewRoles.EditNotes.value
                    )
                  : !isAbleToSeeUIElement(
                      directoryRoles,
                      DirectoryRoles.EditNotes.value
                    )
              }
            />
            <OrganizationsAccordion
              control={control}
              watch={watch}
              errors={errors}
              setValue={setValue}
              getValues={getValues}
              disabled={disableOrganizations}
            />
            <PhonesAccordion
              control={control}
              watch={watch}
              errors={errors}
              disabled={disablePhoneNumbers}
            />
            <RelationsAccordion
              control={control}
              watch={watch}
              errors={errors}
              disabled={disableRelationships}
            />
            <WebsitesAccordion
              control={control}
              errors={errors}
              watch={watch}
              disabled={
                userView
                  ? !isAbleToSeeUIElement(
                      userViewRoles!,
                      UserViewRoles.EditWebsites.value
                    )
                  : !isAbleToSeeUIElement(
                      directoryRoles,
                      DirectoryRoles.EditWebsites.value
                    )
              }
            />
          </Stack>
        </Box>
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={() => {
            const gPanelUser = mutateData();
            mutate(gPanelUser);
          }}
          mutateLoading={updatingUser}
        />
      </Box>
    </>
  );
}
